import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/AuthContext";
import { getData as getCandidates, update as updateCandidate } from "../../../models/CandidatesModel";
import { getData as getCandidateStatus } from "../../../models/CandidateStatusModel";
import { checkPermission } from "../../../helpers/GeneralHelper";
import { apiError } from "../../../helpers/ErrorHelper";
import TableLoader from "../../skeletons/TableLoader";
import { InterviewAddFromCandidate } from "../../modals/InterviewAddFromCandidate";
import KanbanStatusChangeModal from "../../modals/KanbanStatusChangeModal";
import {
  buildCandidateKanbanUpdatePayload,
  buildKanbanColumns,
  getCandidateDisplayName,
  groupCandidatesByStatus,
} from "../../../helpers/RequirementKanbanHelper";

export default function RequirementKanban({ requirement }) {
  const { state } = useAuth();
  const userToken = state.user_data?.user_token;
  const navigate = useNavigate();

  const requirementId = requirement?._requirement_id;
  const clientId = requirement?.requirement_client_id;
  const canEdit = checkPermission("candidates", "edit");

  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [statusChangeModal, setStatusChangeModal] = useState(null);
  const [scheduleModal, setScheduleModal] = useState(null);

  const loadBoard = useCallback(async () => {
    if (!userToken || !requirementId) return;

    setLoading(true);
    try {
      const [statusRes, candidateRes] = await Promise.all([
        getCandidateStatus({ user_token: userToken, page: 1, limit: -1 }),
        getCandidates({
          user_token: userToken,
          page: 1,
          limit: -1,
          candidate_client_id: clientId || "",
          candidate_requirement_id: requirementId,
          include_inactive_mappings: 1,
        }),
      ]);

      setStatuses(statusRes?.candidate_status || []);
      setCandidates(candidateRes?.candidates || []);
    } catch (error) {
      console.error(error);
      toast.error("Could not load Kanban board.");
    }
    setLoading(false);
  }, [userToken, requirementId, clientId]);

  useEffect(() => {
    loadBoard();
  }, [loadBoard]);

  const { groups } = useMemo(
    () => groupCandidatesByStatus(candidates, statuses),
    [candidates, statuses]
  );

  const columns = useMemo(
    () => buildKanbanColumns(statuses, groups),
    [statuses, groups]
  );

  const usesConfiguredPipeline = useMemo(
    () => statuses.some((status) => Number(status.status_show_in_kanban) === 1),
    [statuses]
  );

  const openCandidate = (candidate) => {
    navigate(`/candidates/${candidate.candidate_unique_id}/view`, {
      state: {
        data: candidate,
        module: {
          title: "Candidates",
          singularTitle: "Candidate",
          key: "candidates",
          route: "candidates",
          primaryCol: "_candidate_id",
        },
      },
    });
  };

  const moveCandidateLocally = (candidateId, toStatusId, nextStatusName) => {
    setCandidates((prev) =>
      prev.map((row) =>
        String(row._candidate_id) === String(candidateId)
          ? {
              ...row,
              candidate_status_id: toStatusId === "unassigned" ? "" : Number(toStatusId),
              status_name: nextStatusName,
            }
          : row
      )
    );
  };

  const persistStatusChange = async (candidate, targetStatus, remark = "") => {
    const payload = buildCandidateKanbanUpdatePayload(
      candidate,
      Number(targetStatus._status_id),
      remark
    );
    if (!payload) return false;

    setSavingId(candidate._candidate_id);
    try {
      const response = await updateCandidate(userToken, candidate._candidate_id, payload);
      if (response?.status) {
        moveCandidateLocally(
          candidate._candidate_id,
          String(targetStatus._status_id),
          targetStatus.status_name
        );
        toast.success(`${getCandidateDisplayName(candidate)} → ${targetStatus.status_name}`);
        return true;
      }
      toast.error(response?.message || "Could not update candidate status.");
      return false;
    } catch (error) {
      apiError(error);
      return false;
    } finally {
      setSavingId(null);
    }
  };

  const handleStatusChangeConfirm = async ({ status, remark, schedulesInterview }) => {
    if (!statusChangeModal?.candidate || !status) return;

    const candidate = statusChangeModal.candidate;

    if (schedulesInterview) {
      setStatusChangeModal(null);
      setScheduleModal({
        candidate,
        statusId: Number(status._status_id),
        statusName: status.status_name,
        remark,
      });
      return;
    }

    const saved = await persistStatusChange(candidate, status, remark);
    if (saved) {
      setStatusChangeModal(null);
    }
  };

  if (loading) {
    return (
      <div className="requirement-kanban-loading py-4">
        <TableLoader noOfColumn={4} />
      </div>
    );
  }

  return (
    <div className="requirement-kanban-shell">
      <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-3">
        <div>
          <h6 className="mb-1 fw-semibold text-primary">Candidate Kanban</h6>
          <p className="text-muted small mb-0">
            Pipeline columns show core stages only. Other statuses appear in the last column.{" "}
            {canEdit
              ? "Use Change status on each card — no drag-and-drop. Interview statuses open scheduling before the update is saved."
              : "View only — you do not have permission to change status."}
            {!usesConfiguredPipeline ? (
              <>
                {" "}
                Tip: set <strong>Show in Kanban</strong> on Candidate Status masters to control
                columns.
              </>
            ) : null}
          </p>
        </div>
        <button type="button" className="btn btn-sm btn-light" onClick={loadBoard}>
          <i className="ri-refresh-line me-1" />
          Refresh
        </button>
      </div>

      {candidates.length === 0 ? (
        <div className="requirement-kanban-empty">
          <i className="ri-user-search-line fs-2 text-muted" />
          <p className="mb-0 mt-2 text-muted">No candidates submitted for this requirement yet.</p>
        </div>
      ) : (
        <div className="requirement-kanban-board">
          {columns.map((column) => (
            <div key={column.id} className="requirement-kanban-column">
              <div
                className="requirement-kanban-column-header"
                style={{ borderTopColor: column.theme.accent }}
              >
                <div>
                  <span className="requirement-kanban-column-title">{column.label}</span>
                  {column.schedulesInterview && (
                    <span className="requirement-kanban-column-flag">
                      <i className="ri-calendar-event-line me-1" />
                      Schedules interview
                    </span>
                  )}
                  {column.isOther && (
                    <span className="requirement-kanban-column-flag text-muted">
                      Status changes via card menu
                    </span>
                  )}
                </div>
                <span
                  className="requirement-kanban-column-count"
                  style={{ backgroundColor: column.theme.soft, color: column.theme.accent }}
                >
                  {column.cards.length}
                </span>
              </div>

              <div className="requirement-kanban-column-body">
                {column.cards.length === 0 ? (
                  <div className="requirement-kanban-column-placeholder">No candidates</div>
                ) : (
                  column.cards.map((candidate) => {
                    const isSaving = savingId === candidate._candidate_id;

                    return (
                      <div
                        key={candidate._candidate_id}
                        className={`requirement-kanban-card${isSaving ? " is-saving" : ""}`}
                      >
                        {column.isOther && candidate.status_name ? (
                          <span className="requirement-kanban-card-status-badge">
                            {candidate.status_name}
                          </span>
                        ) : null}

                        <button
                          type="button"
                          className="requirement-kanban-card-link"
                          onClick={() => openCandidate(candidate)}
                        >
                          <span className="requirement-kanban-card-name">
                            {getCandidateDisplayName(candidate)}
                          </span>
                          {candidate.recruiter_name && (
                            <span className="requirement-kanban-card-meta">
                              <i className="ri-user-line me-1" />
                              {candidate.recruiter_name}
                            </span>
                          )}
                          {candidate.candidate_mobile && (
                            <span className="requirement-kanban-card-meta">
                              <i className="ri-phone-line me-1" />
                              {candidate.candidate_mobile}
                            </span>
                          )}
                        </button>

                        {canEdit ? (
                          <div className="requirement-kanban-card-actions">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary w-100"
                              disabled={isSaving}
                              onClick={() => setStatusChangeModal({ candidate })}
                            >
                              <i className="ri-arrow-left-right-line me-1" />
                              Change status
                            </button>
                          </div>
                        ) : null}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <KanbanStatusChangeModal
        show={Boolean(statusChangeModal)}
        onHide={() => setStatusChangeModal(null)}
        candidate={statusChangeModal?.candidate}
        statuses={statuses}
        saving={Boolean(savingId)}
        onConfirm={handleStatusChangeConfirm}
      />

      {scheduleModal && (
        <InterviewAddFromCandidate
          showModal={Boolean(scheduleModal)}
          setShowModal={(open) => {
            if (!open) setScheduleModal(null);
          }}
          candidateData={scheduleModal.candidate}
          statusId={scheduleModal.statusId}
          onDismiss={() => {
            toast.info("Status unchanged — interview was not scheduled.");
          }}
          onSuccess={async () => {
            const saved = await persistStatusChange(
              scheduleModal.candidate,
              {
                _status_id: scheduleModal.statusId,
                status_name: scheduleModal.statusName,
              },
              scheduleModal.remark || ""
            );
            if (saved) {
              await loadBoard();
            }
            setScheduleModal(null);
          }}
        />
      )}
    </div>
  );
}
